import React, { useState, useEffect } from "react";
import { apiClient } from "../../../services/api/apiClient";

function AddPipeFittingModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    name: "",
    productType: "",
    material: "",
    file: null,
  });

  // Add state for product types
  const [productTypes, setProductTypes] = useState([]);
  const [loadingProductTypes, setLoadingProductTypes] = useState(false);

  // Add state for materials
  const [materials, setMaterials] = useState([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);

  // Fetch product types when component mounts
  useEffect(() => {
    const fetchProductTypes = async () => {
      setLoadingProductTypes(true);
      try {
        // Use apiClient for authenticated request
        const response = await apiClient.get("/products/description");
        setProductTypes(response?.data || []);
      } catch (error) {
        console.error("Error fetching product types:", error);
        setProductTypes([]);
      } finally {
        setLoadingProductTypes(false);
      }
    };

    const fetchMaterials = async () => {
      setLoadingMaterials(true);
      try {
        // Use apiClient for authenticated request
        const response = await apiClient.get("/materials");
        setMaterials(response?.data || []);
      } catch (error) {
        console.error("Error fetching materials:", error);
        setMaterials([]);
      } finally {
        setLoadingMaterials(false);
      }
    };

    fetchProductTypes();
    fetchMaterials();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({
      ...prev,
      file: file,
    }));
  };

  const handleSave = async () => {
    try {
      // Validate required fields
      if (!formData.name || !formData.productType || !formData.material) {
        alert(
          "Please fill in all required fields: Product Name, Product Type, and Material"
        );
        return;
      }

      // Create FormData to handle file upload
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("productType", formData.productType);
      formDataToSend.append("material", formData.material);

      // Append file if one is selected
      if (formData.file) {
        formDataToSend.append("file", formData.file);
      }
      console.log("Here is the formData", formData);

      console.log("Here is the formDataToSend", formDataToSend);
      console.log("FormData contents:");
      for (let pair of formDataToSend.entries()) {
        console.log(pair[0]+ ': ' + pair[1]);
      }
      // Use apiClient for authenticated POST request
      const response = await apiClient.post("/products", formDataToSend, {
        headers: {
          // Let browser set Content-Type for FormData
          // 'Content-Type': 'multipart/form-data',
        },
      });
      const savedProduct = response?.data || response;
      console.log("Product saved successfully:", savedProduct);

      // Reset the form data
      setFormData({
        name: "",
        productType: "",
        material: "",
        file: null,
      });

      onClose();

      // Show success message with file upload status
      const successMessage = savedProduct.fileUploaded
        ? "Product saved successfully with attached cutsheet!"
        : "Product saved successfully!";
      alert(successMessage);
    } catch (error) {
      console.error("Error saving product:", error);
      alert(`Error saving product: ${error.message}`);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-2xl shadow-lg w-full max-w-2xl max-h-4/5 overflow-auto flex flex-col">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Add New Product
          </h2>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="flex-1">
          <div className="flex flex-col gap-6 p-5 bg-white rounded-2xl shadow border border-gray-100">
            {/* Product Name */}
            <div className="flex flex-col gap-2">
              <label className="text-gray-900 text-sm font-medium">
                Product Name
              </label>
              <input
                type="text"
                name="name"
                placeholder="Enter product name"
                className="h-10 p-2.5 bg-white rounded-md shadow-inner border border-gray-300"
                value={formData.name}
                onChange={handleInputChange}
              />
            </div>

            {/* Product Type */}
            <div className="flex flex-col gap-2">
              <label className="text-gray-900 text-sm font-medium">
                Product Type
              </label>
              <select
                name="productType"
                className="h-10 p-2.5 bg-white rounded-md shadow-inner border border-gray-300"
                value={formData.productType}
                onChange={handleInputChange}
                disabled={loadingProductTypes}
              >
                <option value="">
                  {loadingProductTypes ? "Loading..." : "Select Product Type"}
                </option>
                {productTypes.map((productType) => (
                  <option key={productType.id} value={productType.id}>
                    {productType.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Material */}
            <div className="flex flex-col gap-2">
              <label className="text-gray-900 text-sm font-medium">
                Material
              </label>
              <select
                name="material"
                className="h-10 p-2.5 bg-white rounded-md shadow-inner border border-gray-300"
                value={formData.material}
                onChange={handleInputChange}
                disabled={loadingMaterials}
              >
                <option value="">
                  {loadingMaterials ? "Loading..." : "Select Material"}
                </option>
                {materials.map((material) => (
                  <option key={material.id} value={material.id}>
                    {material.name}
                  </option>
                ))}
              </select>
            </div>

            {/* File Upload */}
            <div className="flex flex-col gap-2">
              <label className="text-gray-900 text-sm font-medium">
                Upload File
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  id="file-upload"
                  className="hidden"
                  onChange={handleFileChange}
                  accept=".pdf,"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <svg
                    className="w-8 h-8 text-gray-400 mb-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  {formData.file ? (
                    <span className="text-sm text-gray-600">
                      {formData.file.name}
                    </span>
                  ) : (
                    <>
                      <span className="text-sm text-gray-600">
                        Click to upload a file
                      </span>
                      <span className="text-xs text-gray-400">PDF</span>
                    </>
                  )}
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-4 mt-6">
          <button
            type="button"
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            onClick={handleSave}
          >
            Save Product
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddPipeFittingModal;
